import React, { useDeferredValue, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { blogAPI } from '../services/api';
import type { BlogPostSummary } from '../types';
import { canonicalUrlFor, metaDescription } from '../utils/seo';

const PAGE_SIZE = 9;

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [featuredPost, setFeaturedPost] = useState<BlogPostSummary | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [activeTag, setActiveTag] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const deferredQuery = useDeferredValue(searchInput.trim());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    blogAPI
      .listPublicPosts({
        page,
        pageSize: PAGE_SIZE,
        query: deferredQuery,
        tag: activeTag || undefined,
      })
      .then(({ data }) => {
        if (cancelled) return;
        setPosts(data.posts);
        setAvailableTags(data.available_tags ?? []);
        setTotalPages(data.total_pages ?? 1);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load blog posts', err);
        setError('Unable to load blog posts right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, deferredQuery, activeTag]);

  useEffect(() => {
    let cancelled = false;

    blogAPI
      .listPublicPosts({ page: 1, pageSize: 1 })
      .then(({ data }) => {
        if (!cancelled) {
          setFeaturedPost(data.posts[0] ?? null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load featured blog post', err);
          setFeaturedPost(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [deferredQuery, activeTag]);

  return (
    <>
      <SEO
        title="OppFoundry Blog"
        description={metaDescription('Stories, guides, and tactical advice to help service businesses find and win more public-sector opportunities.')}
        canonical={canonicalUrlFor('/blog')}
        og={{
          title: 'OppFoundry Blog',
          description: metaDescription('Tactical writing for service businesses selling into public-sector opportunities.'),
          url: canonicalUrlFor('/blog') ?? undefined,
        }}
      />
      <div className="bg-[radial-gradient(circle_at_top_left,_rgba(15,23,42,0.08),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.14),_transparent_32%),linear-gradient(180deg,_#f8fbff_0%,_#ffffff_42%,_#f8fafc_100%)]">
        <Layout className="max-w-6xl py-12 sm:py-16">
          <section className="px-1 py-2">
            <div className="grid gap-10 lg:grid-cols-[0.34fr_0.66fr] lg:items-start">
              <div className="pt-2">
                <h1 className="max-w-xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                  OppFoundry Blog
                </h1>
                <p className="mt-5 max-w-md text-lg leading-8 text-slate-600">
                  Resources to help you win more bids, interviews with industry pros, feature announcements, and more.
                </p>
              </div>

              {featuredPost ? (
                <Link
                  to={`/blog/${featuredPost.slug}`}
                  className="group grid overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.10)] transition duration-200 hover:-translate-y-1 hover:border-slate-300 md:grid-cols-[1.08fr_0.92fr]"
                >
                  {featuredPost.display_image_url ? (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100 md:aspect-auto md:h-full">
                      <img
                        src={featuredPost.display_image_url}
                        alt={featuredPost.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                  ) : (
                    <div className="flex min-h-[18rem] items-end bg-[linear-gradient(135deg,_#020617,_#1d4ed8_64%,_#38bdf8)] p-8">
                      <p className="max-w-[18rem] text-3xl font-semibold tracking-tight text-white">
                        {featuredPost.title}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col justify-between p-7 sm:p-8">
                    <div>
                      <div className="flex flex-wrap items-center gap-3 text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
                        <span>Latest post</span>
                        <span className="h-1 w-1 rounded-full bg-slate-300" />
                        <span>{formatBlogDate(featuredPost.published_at)}</span>
                      </div>

                      <h2 className="mt-5 max-w-lg text-3xl font-semibold tracking-tight text-slate-950 transition group-hover:text-sky-700">
                        {featuredPost.title}
                      </h2>

                      {featuredPost.subheading && (
                        <p className="mt-4 max-w-lg text-lg leading-8 text-slate-600">
                          {featuredPost.subheading}
                        </p>
                      )}
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                      {featuredPost.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-white/70 p-8 text-sm text-slate-500">
                  Latest post will appear here once something is published.
                </div>
              )}
            </div>
          </section>

          <section className="mt-10 rounded-[1.75rem] border border-slate-200/80 bg-white/90 px-6 py-6 shadow-sm backdrop-blur sm:px-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-xl">
                <label htmlFor="blog-search" className="text-sm font-medium text-slate-700">
                  Search articles
                </label>
                <input
                  id="blog-search"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search titles, subheadings, and article copy"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
              </div>

              <div className="lg:max-w-xl">
                <p className="text-sm font-medium text-slate-700">Filter by tag</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <TagPill active={!activeTag} onClick={() => setActiveTag('')}>
                    All
                  </TagPill>
                  {availableTags.map((tag) => (
                    <TagPill key={tag} active={activeTag === tag} onClick={() => setActiveTag(tag)}>
                      {tag}
                    </TagPill>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mt-10">
            {loading ? (
              <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center text-slate-500">
                Loading blog posts...
              </div>
            ) : error ? (
              <div className="rounded-[2rem] border border-rose-200 bg-rose-50 px-6 py-10 text-sm text-rose-700">
                {error}
              </div>
            ) : posts.length === 0 ? (
              <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center text-slate-500">
                No posts matched that search yet.
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group flex h-full overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_22px_60px_rgba(15,23,42,0.14)]"
                  >
                    <article className="flex h-full w-full flex-col">
                      {post.display_image_url ? (
                        <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                          <img
                            src={post.display_image_url}
                            alt={post.title}
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="flex aspect-[16/10] items-end bg-[linear-gradient(135deg,_#020617,_#1d4ed8_64%,_#38bdf8)] p-6">
                          <p className="max-w-[14rem] text-lg font-semibold tracking-tight text-white">
                            {post.title}
                          </p>
                        </div>
                      )}

                      <div className="flex flex-1 flex-col p-6">
                        <div className="flex flex-wrap items-center gap-3 text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
                          <span>{post.author.name}</span>
                          <span className="h-1 w-1 rounded-full bg-slate-300" />
                          <span>{formatBlogDate(post.published_at)}</span>
                        </div>

                        <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950 transition group-hover:text-sky-700">
                          {post.title}
                        </h2>

                        {post.subheading && (
                          <p className="mt-3 flex-1 text-base leading-7 text-slate-600">
                            {post.subheading}
                          </p>
                        )}

                        <div className="mt-6 flex flex-wrap gap-2">
                          {post.tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {totalPages > 1 && (
            <section className="mt-10 flex items-center justify-between px-1 py-1">
              <div className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page <= 1}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  disabled={page >= totalPages}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </section>
          )}
        </Layout>
      </div>
    </>
  );
};

const TagPill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg border px-3.5 py-2 text-sm font-medium transition ${
      active
        ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
        : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50'
    }`}
  >
    {children}
  </button>
);

function formatBlogDate(value?: string | null): string {
  if (!value) return 'Draft';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default Blog;
